import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ShareDraftButtons from "@/components/ShareDraftButtons";
import AdminDetailsSection from "@/components/vla/AdminDetailsSection";
import GuidelineSection from "@/components/vla/GuidelineSection";
import { GUIDELINES } from "@/components/vla/guidelineCriteria";
import ClientAccountDashboard from "@/pages/ClientAccountDashboard";
import RequestQueue from "@/pages/RequestQueue";
import CertificationReview from "@/pages/CertificationReview";
import LegalAidStatusMonitor from "@/pages/LegalAidStatusMonitor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getSafeBackUrl } from "@/utils";
import {
  ArrowLeft,
  CalendarDays,
  FileText,
  Clipboard,
  FilePen,
  Printer,
  Car,
  FolderOpen,
  X,
  Shield,
  Mail,
  AlarmClock,
  BookOpen,
  Clock,
  FileCheck,
  CreditCard,
  DollarSign,
  Calendar,
  CalendarCheck,
  GitBranch,
  Search,
  DatabaseZap,
  MonitorCog,
  ShieldCheck,
  ChevronDown,
} from "lucide-react";
import { base44 } from "@/api/base44Client";
import { postToClsShell } from "@/lib/portalMessaging";
import { getPortalTabFromPath, getPortalToolPath } from "@/lib/portalToolRoutes";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { actionstepFolderFor } from "@/lib/actionstepFolders";
import { readUploadedFileUrl } from "@/lib/atlasUploadResponse";
import { portalDocumentName } from "@/lib/portalPdf";
import { createGuidelineCertificationPdf } from "@/lib/guidelineCertificationPdf";
import LegalAidForm from "./LegalAidForm";
import VALSIntakeForm from "./VALSIntakeForm";
import VALSAuthorityToAct from "./VALSAuthorityToAct";
import BacksheetToCounsel from "./BacksheetToCounsel";
import VLAReportWorksheet from "./VLAReportWorksheet";
import AdditionalPrepWorksheet from "./AdditionalPrepWorksheet";
import MemoPrecedent from "./MemoPrecedent";
import LacwWordmark from "@/components/LacwWordmark";
import RgaWordmark from "@/components/RgaWordmark";
import ApplyForAid from "./ApplyForAid";
import TravelClaims from "./TravelClaims";
import ConflictCheck from "./ConflictCheck";
import LACWBilling from "./LACWBilling";
import LACWAidDiary from "./LACWAidDiary";
import AidPlanner from "./AidPlanner";
import AidExtensionFlowchart from "./AidExtensionFlowchart";
import JBLPortalGuide from "./JBLPortalGuide";
import { useAuth } from "@/lib/AuthContext";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";
import RoleGuard from "@/components/RoleGuard";
import AdminAuditLogsView from "@/components/AdminAuditLogsView";
import MorningSummary from "@/components/MorningSummary";
import WeeklySummary from "./WeeklySummary";
import MatterWorkspaceView from "@/components/MatterWorkspaceView";
import AppealCostCertificate from "./AppealCostCertificate";
import FeeSlipInvoicePayment from "./FeeSlipInvoicePayment";
import MonthlyTrustReceipts from "./MonthlyTrustReceipts";
import DisbursementsTracker from "./DisbursementsTracker";
import SubmittedInvoices from "./SubmittedInvoices";
import DraftForms from "./DraftForms";
import VALSDiary from "./VALSDiary";
import { DiaryCodesSheet } from "@/components/DiaryCodes";
import RemindersGuide from "@/components/RemindersGuide";
import LACWStaffCalendar from "./LACWStaffCalendar";
import OutlookMail from "./OutlookMail";
import LacwLocalTestBar from "@/components/LacwLocalTestBar";
import ActionstepSimulator from "./ActionstepSimulator";
import AtlasSimulator from "./AtlasSimulator";
import AtlasLetterDownloader from "./AtlasLetterDownloader";
import { INTAKE_EMAIL } from "@/lib/demoConfig";

const BRANDS = {
  eas: {
    appName: "EAS Legal",
    appSubtitle: "Guideline Certification form",
    firmName: "EAS Legal",
    logoUrl:
      "https://easlegal.com.au/wp-content/uploads/2025/11/easlegal-gold-dark@4x-1024x308.png.webp",
    primaryColor: "#C29A5B", // Gold
    secondaryColor: "#1A1F2C", // Navy/Black
    accentColor: "#C29A5B",
    focusShadow: "rgba(194,154,91,0.15)",
  },
  vals: {
    appName: "VALS",
    appSubtitle: "Guideline Certification form",
    firmName: "VALS",
    logoUrl: "https://cdn.brandfetch.io/vals.org.au",
    primaryColor: "#8C1D18", // Ochre Red from artwork
    secondaryColor: "#222020", // Charcoal from artwork
    accentColor: "#6E9EC5", // Soft River Blue from artwork
    focusShadow: "rgba(140,29,24,0.15)",
  },
  lacw: {
    appName: "LACW",
    appSubtitle: "Guideline Certification form",
    firmName: "Law & Advocacy Centre for Women",
    logoUrl: "", // Empty to trigger LacwWordmark in header
    primaryColor: "#5b21b6", // Deep Purple
    secondaryColor: "#1A1F2C", // Navy/Black
    accentColor: "#db2777", // Fuchsia/Pink
    focusShadow: "rgba(91,33,182,0.15)",
  },
  jbl: {
    appName: "JBL",
    appSubtitle: "Guideline Certification form",
    firmName: "Jarrod Behan Law",
    logoUrl: "/jbl-logo.png",
    primaryColor: "#C29A5B", // Gold
    secondaryColor: "#1E232A", // Dark Slate Charcoal
    accentColor: "#C29A5B",
    focusShadow: "rgba(194,154,91,0.15)",
  },
  admin: {
    appName: "CLS Admin",
    appSubtitle: "Central Administrator Portal",
    firmName: "Complete Law Support",
    logoUrl: "", // Triggers fallback text/logo
    primaryColor: "#2563eb", // Blue
    secondaryColor: "#1e293b", // Slate-800
    accentColor: "#3b82f6", // Light Blue
    focusShadow: "rgba(37,99,235,0.15)",
  },
  rga: {
    appName: "RGA",
    appSubtitle: "Robyn Greensill & Associates Portal",
    firmName: "Robyn Greensill and Associates",
    logoUrl: "", // Triggers fallback text/logo
    primaryColor: "#0f766e", // Dark Teal
    secondaryColor: "#0f172a", // Slate-900
    accentColor: "#0d9488", // Teal-600
    focusShadow: "rgba(15,118,110,0.15)",
  },
  counsel: {
    appName: "Counsel Portal",
    appSubtitle: "Barrister & Advocate Workspace",
    firmName: "Counsel Chambers",
    logoUrl: "", // Triggers fallback text/logo
    primaryColor: "#0f172a", // Slate-900/Charcoal
    secondaryColor: "#334155", // Slate-700
    accentColor: "#f59e0b", // Amber/Gold accent
    focusShadow: "rgba(15,23,42,0.15)",
  },
};

// Map tab IDs to clean URL slugs per brand
const getWorkflowDashboardUrl = () => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (
      host.includes("completelawsupport.com") ||
      host.includes("vercel.app")
    ) {
      return "/api/render/operator/dashboard?v=1.5";
    }
  }
  return "http://127.0.0.1:54322/operator/dashboard?v=1.5";
};

function AdminManualEntryForm() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [aidType, setAidType] = useState("");
  const [dateLodged, setDateLodged] = useState("");
  const [selectedClient, setSelectedClient] = useState("rga");
  const [manualStatus, setManualStatus] = useState("aid_submitted");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientName || !aidType) {
      toast.error("Please fill in both Client Name and Aid Type.");
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedNotes = notes;

      // Upload file if selected
      if (file) {
        const getBase64Data = (fileObj) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(fileObj);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (err) => reject(err);
          });

        try {
          const dataUrl = await getBase64Data(file);
          const uploadRes = await fetch(
            "/api/lacw-emails/upload-atlas-letter",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: file.name,
                data: dataUrl,
              }),
            },
          );
          const uploadPayload = await uploadRes.json();
          const uploadedUrl = readUploadedFileUrl(uploadPayload);
          if (uploadPayload.ok && uploadedUrl) {
            const fileLink = `[Attachment: ${file.name}](${uploadedUrl})`;
            updatedNotes = updatedNotes
              ? `${updatedNotes}\n\n${fileLink}`
              : fileLink;
          } else {
            toast.error(
              uploadPayload.error || "Failed to upload attachment file.",
            );
            setIsSubmitting(false);
            return;
          }
        } catch (uploadErr) {
          console.error(uploadErr);
          toast.error("Failed to read the file for upload.");
          setIsSubmitting(false);
          return;
        }
      }

      const formatDate = (isoString) => {
        if (!isoString) return "";
        try {
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return String(isoString);
          return d.toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        } catch {
          return String(isoString);
        }
      };

      // A single crypto-random id, reused for the record and its message reference.
      // `manual_${Date.now()}` collided whenever two files were created in the same
      // millisecond and was trivially guessable by anyone who knew roughly when a
      // record was made.
      const manualId = `manual_${crypto.randomUUID()}`;
      const payload = {
        id: manualId,
        form_type: "manual_email_request",
        draft_name: `New manual file: ${clientName} - ${aidType}`,
        brand: selectedClient,
        saved_by: user?.name || "Admin",
        saved_by_email: user?.email || "admin@completelawsupport.com",
        form_data: {
          message_id: manualId,
          subject: `New manual file: ${clientName} - ${aidType}`,
          snippet: `Aid Type: ${aidType}${dateLodged ? ` • Date Lodged: ${formatDate(dateLodged)}` : ""}`,
          body: `Aid Type: ${aidType}${dateLodged ? ` • Date Lodged: ${formatDate(dateLodged)}` : ""}`,
          from_address: "manual@completelawsupport.com",
          date: dateLodged
            ? new Date(dateLodged).toISOString()
            : new Date().toISOString(),
          status: manualStatus,
          notes: updatedNotes,
          client_id: selectedClient,
          is_manual: true,
        },
      };

      const res = await fetch("/api/vals-drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Manual file request created successfully!");
        setClientName("");
        setAidType("");
        setDateLodged("");
        setManualStatus("aid_submitted");
        setNotes("");
        setFile(null);
        const fileInput = document.getElementById("adminManualFileInput");
        if (fileInput) fileInput.value = "";
        setIsOpen(false);
      } else {
        toast.error("Failed to create manual file request.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating manual file request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">➕</span>
          <div>
            <h3 className="font-bold text-slate-800 text-sm">
              Add Manual Legal Aid File Request
            </h3>
            <p className="text-xs text-slate-500">
              Create a manual lodge entry for RGA, LACW, or VALS.
            </p>
          </div>
        </div>
        <span className="text-xs text-slate-400 font-bold">
          {isOpen ? "Hide Form [-]" : "Expand Form [+]"}
        </span>
      </button>

      {isOpen && (
        <form
          onSubmit={handleSubmit}
          className="p-6 border-t border-slate-100 bg-[#fafbfc] space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
                Select Client Domain *
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="rga">Robyn Greensill & Associates (RGA)</option>
                <option value="lacw">
                  Law and Advocacy Centre for Women (LACW)
                </option>
                <option value="vals">
                  Victorian Aboriginal Legal Service (VALS)
                </option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
                Client Name *
              </label>
              <input
                type="text"
                required
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-655 uppercase tracking-wide">
                Aid Type *
              </label>
              <input
                type="text"
                required
                value={aidType}
                onChange={(e) => setAidType(e.target.value)}
                placeholder="e.g. Criminal Law, Family Law"
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
                Date Lodged
              </label>
              <input
                type="date"
                value={dateLodged}
                onChange={(e) => setDateLodged(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
                Upload Aid Request File (PDF/Image)
              </label>
              <input
                id="adminManualFileInput"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
              Status Option
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <label
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  manualStatus === "aid_submitted"
                    ? "border-indigo-500 bg-indigo-50/30 text-indigo-900 font-semibold"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="adminManualStatus"
                  value="aid_submitted"
                  checked={manualStatus === "aid_submitted"}
                  onChange={() => setManualStatus("aid_submitted")}
                  className="accent-indigo-600"
                />
                <span className="text-sm">Aid Submitted</span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  manualStatus === "aid_not_submitted"
                    ? "border-indigo-500 bg-indigo-50/30 text-indigo-900 font-semibold"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="adminManualStatus"
                  value="aid_not_submitted"
                  checked={manualStatus === "aid_not_submitted"}
                  onChange={() => setManualStatus("aid_not_submitted")}
                  className="accent-indigo-600"
                />
                <span className="text-sm">Aid not submitted</span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  manualStatus === "pending_in_atlas"
                    ? "border-indigo-500 bg-indigo-50/30 text-indigo-900 font-semibold"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="adminManualStatus"
                  value="pending_in_atlas"
                  checked={manualStatus === "pending_in_atlas"}
                  onChange={() => setManualStatus("pending_in_atlas")}
                  className="accent-indigo-600"
                />
                <span className="text-sm">Pending in ATLAS</span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  manualStatus === "waiting_to_be_submitted_in_atlas"
                    ? "border-indigo-500 bg-indigo-50/30 text-indigo-900 font-semibold"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="adminManualStatus"
                  value="waiting_to_be_submitted_in_atlas"
                  checked={manualStatus === "waiting_to_be_submitted_in_atlas"}
                  onChange={() =>
                    setManualStatus("waiting_to_be_submitted_in_atlas")
                  }
                  className="accent-indigo-600"
                />
                <span className="text-sm">Waiting to be lodged</span>
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-650 uppercase tracking-wide">
              Notes / Comments
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional comments or notes about this file..."
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-vertical"
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-indigo-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm text-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all flex items-center gap-2 ${
                isSubmitting ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting
                ? "Creating Manual Request..."
                : "Create Manual File Request"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function StandAloneGuidelines({ brandType, initialTab }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [backUrl, setBackUrl] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [toolSearch, setToolSearch] = useState("");
  // Admin home sections opened from their minimised state (ADMIN_GROUPS collapsedByDefault).
  const [openAdminGroups, setOpenAdminGroups] = useState(() => new Set());

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const back = params.get("back");
    if (back) {
      setBackUrl(getSafeBackUrl(back));
    }
  }, []);

  // Load existing draft from URL param or active clientStorage draft
  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const currentBrand =
    brandType === "admin" ||
    path.includes("admin-portal") ||
    path.includes("admin-")
      ? "admin"
      : brandType === "vals" ||
          path.includes("vals-portal") ||
          path.includes("vals-")
        ? "vals"
        : brandType === "lacw" ||
            path.includes("lacw-portal") ||
            path.includes("lacw-")
          ? "lacw"
          : brandType === "jbl" ||
              path.includes("jbl-portal") ||
              path.includes("jbl-")
            ? "jbl"
            : brandType === "rga" ||
                path.includes("rga-portal") ||
                path.includes("rga-")
              ? "rga"
              : brandType === "counsel" ||
                  path.includes("counsel-portal") ||
                  path.includes("counsel-")
                ? "counsel"
                : "eas";
  const brandObj = BRANDS[currentBrand];
  const primary = brandObj.primaryColor;
  const secondary = brandObj.secondaryColor;
  const focusShadow = brandObj.focusShadow;

  const isPortalMode =
    path.includes("vals-portal") ||
    path.includes("eas-portal") ||
    path.includes("lacw-portal") ||
    path.includes("jbl-portal") ||
    path.includes("admin-portal") ||
    path.includes("rga-portal") ||
    path.includes("counsel-portal") ||
    path.includes("vals-") ||
    path.includes("eas-") ||
    path.includes("lacw-") ||
    path.includes("jbl-") ||
    path.includes("admin-") ||
    path.includes("rga-") ||
    path.includes("counsel-");
  const brandPortalTabs =
    currentBrand === "vals"
      ? [
          {
            id: "matter-workspace",
            label: "VALS Matter Workspace",
            description:
              "Private browser-session workspace for matter drafts, tasks, timelines, and documents.",
            icon: FolderOpen,
          },
          {
            id: "certification",
            label: "VALS Guideline Certification",
            description:
              "Complete and export VLA guideline compliance certificates for VALS matters.",
            icon: Printer,
          },
          {
            id: "aid-form",
            label: "VALS Legal Aid Application",
            description:
              "Complete and export Victoria Legal Aid application forms for VALS matters.",
            icon: FileText,
          },
          {
            id: "apply-aid",
            label: "VALS Apply for Aid",
            description:
              "Complete and print the internal aid request worksheet for VALS matters.",
            icon: FileText,
          },
          {
            id: "vals-emails",
            label: "VALS Legal Aid Status Monitor",
            description:
              "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
            icon: FileCheck,
            // Its file requests are now the Aid Planner's Applications list. The tile
            // is hidden; the address still opens it as a fallback.
            hidden: true,
          },
          {
            id: "fee-slip-invoice-payment",
            label: "VALS Fee slip and Invoice payment",
            description:
              "Advise outstanding payments for clerk fee slips and invoice payments.",
            icon: CreditCard,
          },
          {
            id: "submitted-invoices",
            label: "Submitted invoices",
            description:
              "See whether the fee slips, invoices, disbursements and travel claims you sent in have been paid.",
            icon: CreditCard,
          },
          {
            id: "monthly-trust-receipts",
            label: "VALS Disbursements",
            description:
              "Upload and track psychological, medical and other report invoices, interpreters and transcripts.",
            icon: DollarSign,
          },
          {
            id: "appeal-cost-certificate",
            label: "VALS Appeal Cost Certificate Lodgment",
            description:
              "Lodge and track court appeal cost certificates for VALS matters.",
            icon: FileCheck,
          },
          {
            id: "draft-forms",
            label: "VALS Draft Forms",
            description: "Restore and edit saved client form drafts.",
            icon: FolderOpen,
          },
          {
            id: "intake",
            label: "VALS Criminal Intake",
            description:
              "Complete and save client intake details for VALS criminal law matters.",
            icon: Clipboard,
          },
          {
            id: "authority",
            label: "VALS Authority to Act",
            description:
              "Complete and sign client authority to act and disclosure forms for VALS matters.",
            icon: FilePen,
          },
          {
            id: "backsheet",
            label: "VALS Backsheet to Counsel",
            description:
              "Prepare and print backsheet instructions for counsel for VALS matters.",
            icon: FileText,
          },
          {
            id: "medical-worksheet",
            label: "VALS Medical Worksheet",
            description:
              "Complete the VLA medical, psychologist, or psychiatrist report worksheet.",
            icon: FileText,
          },
          {
            id: "memo-precedent",
            label: "VALS Memo Precedent",
            description:
              "Create and print funding request memos for psychiatric or medical reports.",
            icon: FilePen,
          },
          {
            id: "additional-prep",
            label: "VALS Additional Prep",
            description:
              "Complete and print additional preparation worksheets for counsel or solicitors.",
            icon: FileText,
          },
          {
            id: "vals-diary",
            label: "VALS Diary",
            description:
              "Provide details for each appearance type, verify legal aid status, and confirm aid numbers.",
            icon: Calendar,
          },
          {
            id: "reminders-guide",
            label: "Grant reminders guide",
            description:
              "How the portal works out when an extension or new grant is due, and what to do with a reminder.",
            icon: AlarmClock,
            // Opened from each reminders list (the diary, the Aid Planner).
            hidden: true,
          },
          {
            id: "diary-codes",
            label: "Diary codes",
            description:
              "The universal codes to type for each listing in the weekly diary, and the fee each uses up.",
            icon: BookOpen,
            // Opened from the tools that read diary codes (the diary).
            hidden: true,
          },
          {
            id: "aid-planner",
            label: "VALS Aid Planner",
            description:
              "Upload VLA approval letters, extensions and claims, track what each grant allows, and see when an extension or new grant is due.",
            icon: CalendarDays,
          },
        ]
      : currentBrand === "lacw"
        ? [
            {
              id: "atlas-letter-downloader",
              label: "ATLAS Letter Downloader",
              description: "Download, rename, match and file ATLAS letters into Actionstep matters.",
              icon: FileCheck,
              testing: true,
            },
            {
              id: "actionstep-simulator",
              label: "Actionstep Simulator",
              description: "Test portal links against fictional matters without a live Actionstep account.",
              icon: DatabaseZap,
              testing: true,
            },
            {
              id: "request-queue",
              label: "Request Queue",
              description:
                "Requests raised from these tools, and what has been done with them.",
              icon: Clipboard,
            },
            {
              id: "atlas-simulator",
              label: "ATLAS Simulator",
              description: "Use a fictional ATLAS inbox and clickable test workflows.",
              icon: MonitorCog,
              testing: true,
            },
            {
              id: "outlook-mail",
              label: "Outlook Mail & Calendar",
              description:
                "Read Microsoft 365 email and manage Outlook appointments in one workspace.",
              icon: Mail,
            },
            {
              id: "staff-calendar",
              label: "LACW Staff Calendar",
              description:
                "Central staff diary for listed matters, appearances, allocations, and billing flags.",
              icon: Calendar,
            },
            {
              id: "reminders-guide",
              label: "Grant reminders guide",
              description:
                "How the portal works out when an extension or new grant is due, and what to do with a reminder.",
              icon: AlarmClock,
              // Opened from each reminders list (the diary, the Aid Planner).
              hidden: true,
            },
            {
              id: "diary-codes",
              label: "Diary codes",
              description:
                "The universal codes to type for each listing in the weekly diary, and the fee each uses up.",
              icon: BookOpen,
              // Opened from the tools that read diary codes (the diaries, the billing sheet).
              hidden: true,
            },
            {
              id: "aid-diary",
              label: "LACW Aid Status Diary",
              description:
                "Each week's legal aid status for every listed appearance, as Complete Law Support has confirmed it.",
              icon: CalendarCheck,
            },
            {
              id: "aid-planner",
              label: "LACW Aid Planner",
              description:
                "Upload VLA approval letters, extensions and claims, track what each grant allows, and see when an extension or new grant is due.",
              icon: CalendarDays,
            },
            {
              id: "aid-extension-flowchart",
              label: "Aid Extension Flowchart",
              description:
                "Follow the grant, scope and fee checks to see whether an ATLAS extension is needed.",
              icon: GitBranch,
              // Now inside each Aid Planner matter; the address still works for the diary's link.
              hidden: true,
            },
            {
              id: "certification",
              label: "Guideline Certification",
              description:
                "Complete and export VLA guideline compliance certificates for LACW matters.",
              icon: Printer,
            },
            {
              id: "aid-form",
              label: "Legal Aid Application",
              description:
                "Complete and export Victoria Legal Aid application forms for LACW matters.",
              icon: FileText,
            },
            {
              id: "apply-aid",
              label: "Apply for Aid",
              description:
                "Complete and print the internal aid request worksheet for LACW matters.",
              icon: FileText,
            },
            {
              id: "backsheet",
              label: "Backsheet to Counsel",
              description:
                "Prepare and print backsheet instructions for counsel for LACW matters.",
              icon: FileText,
            },
            {
              id: "medical-worksheet",
              label: "Medical Worksheet",
              description:
                "Complete the VLA medical, psychologist, or psychiatrist report worksheet.",
              icon: FileText,
            },
            {
              id: "memo-precedent",
              label: "Memo Precedent",
              description:
                "Create and print funding request memos for psychiatric or medical reports.",
              icon: FilePen,
            },
            {
              id: "travel-claim",
              label: "Travel Claim Form",
              description:
                "Complete and export LACW travel allowance and expense claim forms.",
              icon: Car,
            },
            {
              id: "appeal-cost-certificate",
              label: "Appeal Cost Certificate Lodgment",
              description:
                "Lodge and track court appeal cost certificates for LACW matters.",
              icon: FileCheck,
            },
            {
              id: "lacw-emails",
              label: "Legal Aid Status Monitor",
              description:
                "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
              icon: FileCheck,
              // Its file requests are now the Aid Planner's Applications list. The tile
              // is hidden; the address still opens it as a fallback.
              hidden: true,
            },
            {
              id: "fee-slip-invoice-payment",
              label: "Fee slip and Invoice payment",
              description:
                "Advise outstanding payments for clerk fee slips and invoice payments.",
              icon: CreditCard,
            },
            {
              id: "submitted-invoices",
              label: "Submitted invoices",
              description:
                "See whether the fee slips, invoices, disbursements and travel claims you sent in have been paid.",
              icon: CreditCard,
            },
            {
              id: "monthly-trust-receipts",
              label: "Disbursements",
              description:
                "See LACW's disbursements, their invoices and receipts, and which have been paid.",
              icon: DollarSign,
            },
            {
              id: "draft-forms",
              label: "LACW Draft Forms",
              description: "Restore and edit saved client form drafts.",
              icon: FolderOpen,
            },
            {
              id: "additional-prep",
              label: "Additional Prep Worksheet",
              description:
                "Complete and print additional preparation worksheets for counsel or solicitors.",
              icon: FileText,
            },
          ]
        : currentBrand === "jbl"
          ? [
              {
                id: "matter-workspace",
                label: "JBL Matter Workspace",
                description:
                  "Private browser-session workspace for matter drafts, tasks, timelines, and documents.",
                icon: FolderOpen,
              },
              {
                id: "certification",
                label: "JBL Guideline Certification",
                description:
                  "Complete and export VLA guideline compliance certificates for JBL matters.",
                icon: Printer,
              },
              {
                id: "aid-form",
                label: "JBL Legal Aid Application",
                description:
                  "Complete and export Victoria Legal Aid application forms for JBL matters.",
                icon: FileText,
              },
              {
                id: "medical-worksheet",
                label: "JBL Medical Worksheet",
                description:
                  "Complete the VLA medical, psychologist, or psychiatrist report worksheet.",
                icon: FileText,
              },
              {
                id: "conflict-check",
                label: "JBL Conflict Check",
                description:
                  "Upload documents to scan and check against conflict databases.",
                icon: Shield,
              },
              {
                id: "draft-forms",
                label: "JBL Draft Forms",
                description: "Restore and edit saved client form drafts.",
                icon: FolderOpen,
              },
              {
                id: "training-guide",
                label: "JBL Portal Guide",
                description:
                  "Learn how to use, save drafts, and email submissions from each tool on the JBL portal.",
                icon: BookOpen,
              },
              {
                id: "additional-prep",
                label: "JBL Additional Prep",
                description:
                  "Complete and print additional preparation worksheets for counsel or solicitors.",
                icon: FileText,
              },
            ]
          : currentBrand === "admin"
            ? [
                {
                  id: "matter-workspace",
                  label: "Admin Matter Workspace",
                  description:
                    "Private browser-session workspace for matter drafts, tasks, timelines, and documents.",
                  icon: FolderOpen,
                },
                {
                  id: "inbox",
                  label: "Admin inbox",
                  description:
                    "Requests submitted from the portal tools, such as Apply for Aid, across every firm.",
                  icon: Mail,
                },
                {
                  id: "weekly-summary",
                  label: "Weekly summary",
                  description:
                    "What was actioned for VALS, LACW, EAS and RGA in a week, to print and give to the firm.",
                  icon: CalendarCheck,
                },
                {
                  id: "deadline-guard",
                  label: "Deadline Guard",
                  description:
                    "Detect critical deadline signals and invoice issues within Gmail threads using automated risk analysis.",
                  icon: Clock,
                },
                {
                  id: "lacw-client-account",
                  label: "LACW Client Account",
                  description:
                    "Open LACW fee slips, disbursements and grouped email requests in a dedicated workspace.",
                  icon: FolderOpen,
                },
                {
                  id: "vals-client-account",
                  label: "VALS Client Account",
                  description:
                    "Open VALS fee slips, disbursements and grouped email requests in a dedicated workspace.",
                  icon: FolderOpen,
                },
                {
                  id: "eas-client-account",
                  label: "EAS Client Account",
                  description:
                    "Open EAS fee slips, disbursements and grouped email requests in a dedicated workspace.",
                  icon: FolderOpen,
                },
                {
                  id: "billing",
                  label: "Billing Sheet",
                  description: "Upload a diary PDF to process ATLAS claims.",
                  icon: FileText,
                },
                {
                  id: "lacw-aid-planner",
                  label: "LACW Aid Planner",
                  description:
                    "LACW's matters: upload VLA letters, record claims and see when an extension or new grant is due. Saved under LACW, so LACW staff see the same matters.",
                  icon: CalendarDays,
                },
                {
                  id: "lacw-staff-calendar",
                  label: "LACW Staff Calendar",
                  description:
                    "LACW's central staff diary: listed matters, appearances, who is appearing and billing flags. Entries are saved under LACW, so LACW staff see the same calendar.",
                  icon: Calendar,
                },
                {
                  id: "appeal-cost-certificate",
                  label: "RGA Appeal Cost Certificates",
                  description:
                    "Review and process Robyn Greensill & Associates appeal cost certificates.",
                  icon: FileCheck,
                },
                {
                  id: "lacw-appeal-cost-certificate",
                  label: "LACW Appeal Cost Certificates",
                  description:
                    "Lodge, review and process LACW appeal cost certificates.",
                  icon: FileCheck,
                },
                {
                  id: "vals-appeal-cost-certificate",
                  label: "VALS Appeal Cost Certificates",
                  description:
                    "Lodge, review and process VALS appeal cost certificates.",
                  icon: FileCheck,
                },
                {
                  id: "eas-appeal-cost-certificate",
                  label: "EAS Appeal Cost Certificates",
                  description:
                    "Lodge, review and process EAS appeal cost certificates.",
                  icon: FileCheck,
                },
                {
                  id: "lacw-fee-slip-invoice-payment",
                  label: "LACW Clerk Fee Slips",
                  description:
                    "Review advised payment updates for clerk fee slips and invoices.",
                  icon: CreditCard,
                },
                {
                  id: "lacw-trust-receipts",
                  label: "LACW Disbursements",
                  description:
                    "Upload and track psychological, medical and other report invoices, interpreters and transcripts.",
                  icon: DollarSign,
                },
                {
                  id: "lacw-emails",
                  label: "LACW New File Emails",
                  description:
                    "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
                  icon: Mail,
                },
                {
                  id: "rga-fee-slip-invoice-payment",
                  label: "RGA Clerk Fee Slips",
                  description:
                    "Review advised payment updates for clerk fee slips and invoices.",
                  icon: CreditCard,
                },
                {
                  id: "rga-trust-receipts",
                  label: "RGA Disbursements",
                  description:
                    "Review and manage Robyn Greensill & Associates monthly trust receipts.",
                  icon: DollarSign,
                },
                {
                  id: "rga-emails",
                  label: "RGA New File Emails",
                  description:
                    "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
                  icon: Mail,
                },
                {
                  id: "vals-fee-slip-invoice-payment",
                  label: "VALS Clerk Fee Slips",
                  description:
                    "Review advised payment updates for clerk fee slips and invoices.",
                  icon: CreditCard,
                },
                {
                  id: "vals-trust-receipts",
                  label: "VALS Disbursements",
                  description: "Upload and track psychological, medical and other report invoices, interpreters and transcripts.",
                  icon: DollarSign,
                },
                {
                  id: "vals-diary",
                  label: "VALS Diary",
                  description:
                    "Provide details for each appearance type, verify legal aid status, and confirm aid numbers.",
                  icon: Calendar,
                },
                {
                  id: "vals-emails",
                  label: "VALS New File Emails",
                  description:
                    "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
                  icon: Mail,
                },
                {
                  id: "eas-fee-slip-invoice-payment",
                  label: "EAS Clerk Fee Slips",
                  description:
                    "Review advised payment updates for clerk fee slips and invoices.",
                  icon: CreditCard,
                },
                {
                  id: "eas-trust-receipts",
                  label: "EAS Disbursements",
                  description: "Upload and track psychological, medical and other report invoices, interpreters and transcripts.",
                  icon: DollarSign,
                },
                {
                  id: "eas-diary",
                  label: "EAS Diary",
                  description:
                    "Provide details for each appearance type, verify legal aid status, and confirm aid numbers.",
                  icon: Calendar,
                },
                {
                  id: "eas-emails",
                  label: "EAS New File Emails",
                  description:
                    "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
                  icon: Mail,
                },
                {
                  id: "draft-forms",
                  label: "Admin Draft Forms",
                  description: "Manage and restore saved client drafts.",
                  icon: FolderOpen,
                },
                {
                  id: "audit-logs",
                  label: "System Audit Logs",
                  description:
                    "View security audit trails and manage system database backups.",
                  icon: Shield,
                },
              ]
            : currentBrand === "rga"
              ? [
                  {
                    id: "certification",
                    label: "RGA Guideline Certification",
                    description:
                      "Complete and export VLA guideline compliance certificates for RGA matters.",
                    icon: Printer,
                  },
                  {
                    id: "aid-form",
                    label: "RGA Legal Aid Application",
                    description:
                      "Complete and export Victoria Legal Aid application forms for RGA matters.",
                    icon: FileText,
                  },
                  {
                    id: "apply-aid",
                    label: "RGA Apply for Aid",
                    description:
                      "Complete and print the internal aid request worksheet for RGA matters.",
                    icon: FileText,
                  },
                  {
                    id: "rga-emails",
                    label: "RGA Legal Aid Status Monitor",
                    description:
                      "Lodge ATLAS letters and monitor live Victoria Legal Aid application statuses.",
                    icon: FileCheck,
                  },
                  {
                    id: "fee-slip-invoice-payment",
                    label: "RGA Fee slip and Invoice payment",
                    description:
                      "Advise outstanding payments for clerk fee slips and invoice payments.",
                    icon: CreditCard,
                  },
                  {
                    id: "monthly-trust-receipts",
                    label: "RGA Monthly Trust Receipts",
                    description:
                      "Upload and manage monthly bank statements and receipt records from the Trust Account.",
                    icon: DollarSign,
                  },
                  {
                    id: "appeal-cost-certificate",
                    label: "RGA Appeal Cost Certificate Lodgment",
                    description:
                      "Lodge and track court appeal cost certificates for RGA matters.",
                    icon: FileCheck,
                  },
                  {
                    id: "medical-worksheet",
                    label: "RGA Medical Worksheet",
                    description:
                      "Complete the VLA medical, psychologist, or psychiatrist report worksheet.",
                    icon: FileText,
                  },
                  {
                    id: "memo-precedent",
                    label: "RGA Memo Precedent",
                    description:
                      "Create and print funding request memos for psychiatric or medical reports.",
                    icon: FilePen,
                  },
                  {
                    id: "draft-forms",
                    label: "RGA Draft Forms",
                    description: "Restore and edit saved client form drafts.",
                    icon: FolderOpen,
                  },
                  {
                    id: "additional-prep",
                    label: "RGA Additional Prep",
                    description:
                      "Complete and print additional preparation worksheets for counsel or solicitors.",
                    icon: FileText,
                  },
                ]
              : currentBrand === "counsel"
                ? [
                    {
                      id: "matter-workspace",
                      label: "Counsel Matter Workspace",
                      description:
                        "Private browser-session workspace for matter drafts, tasks, timelines, and documents.",
                      icon: FolderOpen,
                    },
                    {
                      id: "backsheet",
                      label: "Briefs and Backsheets",
                      description:
                        "Review briefs and complete outcome memos for instructing solicitors.",
                      icon: FileText,
                    },
                    {
                      id: "travel-claim",
                      label: "Travel Claim Form",
                      description:
                        "Complete and export travel allowance and expense claim forms.",
                      icon: Car,
                    },
                    {
                      id: "draft-forms",
                      label: "Counsel Draft Forms",
                      description: "Restore and edit saved client form drafts.",
                      icon: FolderOpen,
                    },
                    {
                      id: "additional-prep",
                      label: "Additional Prep Worksheet",
                      description:
                        "Complete and print additional preparation worksheets.",
                      icon: FileText,
                    },
                  ]
                : [
                    {
                      id: "matter-workspace",
                      label: "EAS Matter Workspace",
                      description:
                        "Private browser-session workspace for matter drafts, tasks, timelines, and documents.",
                      icon: FolderOpen,
                    },
                    {
                      id: "certification",
                      label: "EAS Guideline Certification",
                      description:
                        "Complete and export VLA guideline compliance certificates for EAS matters.",
                      icon: Printer,
                    },
                    {
                      id: "aid-form",
                      label: "EAS Legal Aid Application",
                      description:
                        "Complete and export Victoria Legal Aid application forms for EAS matters.",
                      icon: FileText,
                    },
                    {
                      id: "apply-aid",
                      label: "EAS Apply for Aid",
                      description:
                        "Complete and print the internal aid request worksheet for EAS matters.",
                      icon: FileText,
                    },
                    {
                      id: "eas-diary",
                      label: "EAS Diary",
                      description:
                        "Provide details for each appearance type, verify legal aid status, and confirm aid numbers.",
                      icon: Calendar,
                    },
                    {
                      id: "eas-emails",
                      label: "EAS New File Emails",
                      description:
                        "New legal aid file requests, from the request email to VLA's decision.",
                      icon: Mail,
                    },
                    {
                      id: "fee-slip-invoice-payment",
                      label: "EAS Fee slip and Invoice payment",
                      description:
                        "Advise outstanding payments for clerk fee slips and invoice payments.",
                      icon: CreditCard,
                    },
                    {
                      id: "submitted-invoices",
                      label: "Submitted invoices",
                      description:
                        "See whether the fee slips, invoices, disbursements and travel claims you sent in have been paid.",
                      icon: CreditCard,
                    },
                    {
                      id: "monthly-trust-receipts",
                      label: "EAS Disbursements",
                      description:
                        "Upload and track psychological, medical and other report invoices, interpreters and transcripts.",
                      icon: DollarSign,
                    },
                    {
                      id: "appeal-cost-certificate",
                      label: "EAS Appeal Cost Certificate Lodgment",
                      description:
                        "Lodge and track court appeal cost certificates for EAS matters.",
                      icon: FileCheck,
                    },
                    {
                      id: "draft-forms",
                      label: "EAS Draft Forms",
                      description: "Restore and edit saved client form drafts.",
                      icon: FolderOpen,
                    },
                  ];

  // Every firm has certification: senior staff certify legal aid applications before they
  // are lodged. The administrator chooses each firm's certifiers on the same page.
  const portalTabs = [
    ...brandPortalTabs,
    {
      id: "certifications",
      label: "Certifications",
      description:
        currentBrand === "admin"
          ? "Choose who certifies legal aid applications at each firm, and see what is waiting and what was decided."
          : "Legal aid applications waiting for senior certification, and what was decided.",
      icon: ShieldCheck,
    },
  ];

  const [activePortalTab, setActivePortalTab] = useState(() => {
    // Priority: initialTab prop (from route) > ?tab= query param > default
    if (initialTab && portalTabs.some((t) => t.id === initialTab)) {
      return initialTab;
    }
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && portalTabs.some((t) => t.id === tabParam)) {
      return tabParam;
    }
    return isPortalMode ? "dashboard" : "certification";
  });

  const handleTabChange = (tabId) => {
    setActivePortalTab(tabId);
    const newPath =
      tabId === "dashboard"
        ? `/${currentBrand}-portal`
        : getPortalToolPath(currentBrand, tabId);
    // Notify the parent Squarespace page to update the browser URL
    try {
      postToClsShell({ type: "PORTAL_NAVIGATE", path: newPath });
    } catch (e) {}
    // Also try direct pushState (works if same-origin)
    try {
      window.top.history.pushState(null, "", newPath);
    } catch (e) {
      navigate(newPath);
    }
  };

  // handleTabChange moves the address without React Router, so the browser's Back and
  // Forward buttons would change the address and leave the tool on screen. Show the tab
  // the address names instead.
  const portalTabIdsRef = useRef([]);
  portalTabIdsRef.current = portalTabs.map((t) => t.id);
  useEffect(() => {
    const showTabForAddress = (win) => () => {
      const tabId = getPortalTabFromPath(
        currentBrand,
        win.location.pathname,
        portalTabIdsRef.current,
      );
      if (tabId) setActivePortalTab(tabId);
    };
    const windows = [window];
    try {
      if (window.top !== window && window.top.location.pathname) windows.push(window.top);
    } catch (e) {}
    const listeners = windows.map((win) => {
      const listener = showTabForAddress(win);
      win.addEventListener("popstate", listener);
      return () => win.removeEventListener("popstate", listener);
    });
    return () => listeners.forEach((remove) => remove());
  }, [currentBrand]);

  const onTabChange = (tabId, draftId, matterId) => {
    let query = "";
    if (draftId) query = `?id=${encodeURIComponent(draftId)}`;
    else if (matterId) query = `?matter_id=${encodeURIComponent(matterId)}`;

    const newPath = `${getPortalToolPath(currentBrand, tabId)}${query}`;

    setActivePortalTab(tabId);
    navigate(newPath);
    try {
      postToClsShell({ type: "PORTAL_NAVIGATE", path: newPath });
    } catch (e) {}
  };

  const today = new Date().toISOString().split("T")[0];
  const mainContentRef = useRef(null);

  const getSubTitle = () => {
    if (activePortalTab === "certification")
      return "Guideline Certification form";
    if (activePortalTab === "aid-form") return "Legal Aid Application";
    if (activePortalTab === "apply-aid") return "Apply for Aid";
    if (activePortalTab === "intake") return "Criminal Intake";
    if (activePortalTab === "authority") return "Authority to Act";
    if (activePortalTab === "backsheet")
      return currentBrand === "counsel"
        ? "Briefs and Backsheets"
        : "Backsheet to Counsel";
    if (activePortalTab === "medical-worksheet")
      return "Medical Report Worksheet";
    if (activePortalTab === "memo-precedent") return "Memo Precedent";
    if (activePortalTab === "travel-claim") return "Travel Claim";
    if (activePortalTab === "submitted-invoices") return "Submitted invoices";
    if (activePortalTab === "conflict-check") return "Conflict Check";
    if (activePortalTab === "billing") return "Billing Sheet";
    if (activePortalTab === "aid-diary") return "Aid Status Diary";
    if (activePortalTab === "diary-codes") return "Diary codes";
    if (activePortalTab === "reminders-guide") return "Grant reminders guide";
    if (activePortalTab === "aid-planner") return "Aid Planner";
    if (activePortalTab === "lacw-aid-planner") return "LACW Aid Planner";
    if (activePortalTab === "lacw-staff-calendar") return "LACW Staff Calendar";
    if (activePortalTab === "aid-extension-flowchart")
      return "Aid Extension Flowchart";
    if (activePortalTab === "deadline-guard") return "Deadline Guard";
    if (activePortalTab === "lacw-client-account")
      return "LACW Client Account";
    if (activePortalTab === "vals-client-account")
      return "VALS Client Account";
    return brandObj.appSubtitle;
  };

  const emptyForm = {
    client_name: "",
    client_dob: "",
    file_ref: "",
    lawyer: "",
    certified_by: "",
    cert_date: today,
    notes: "",
    guideline: "",
    criteria: {},
    signature: "",
    waiver_applies: false,
  };

  const [form, setForm] = useState(emptyForm);
  const [boeFile, setBoeFile] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [prefillId, setPrefillId] = useState("");

  const [hasActiveDemographics, setHasActiveDemographics] = useState(false);

  useEffect(() => {
    try {
      const brandKey = currentBrand ? `${currentBrand}_` : "";
      const demographicsKey = `${brandKey}active_client_demographics`;
      const activeStr = clientStorage.getItem(demographicsKey);
      setHasActiveDemographics(!!activeStr);
    } catch (e) {
      console.error(e);
    }
  }, [currentBrand]);

  const prefillFromActiveDemographics = () => {
    try {
      const brandKey = currentBrand ? `${currentBrand}_` : "";
      const demographicsKey = `${brandKey}active_client_demographics`;
      const activeStr = clientStorage.getItem(demographicsKey);
      if (activeStr) {
        const client = JSON.parse(activeStr);
        setForm((prev) => ({
          ...prev,
          client_name:
            `${client.first_name || ""} ${client.surname || ""}`.trim() ||
            prev.client_name,
          client_dob: client.dob || prev.client_dob,
          file_ref: client.file_no || prev.file_ref,
          lawyer: client.lawyer || prev.lawyer,
        }));
        toast.success(
          `Prefilled details from active profile: ${client.first_name || "Client"}.`,
        );
      } else {
        toast.error("No active client intake profile found.");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [completedVlas, setCompletedVlas] = useState([]);

  useEffect(() => {
    const loadVlas = () => {
      try {
        const brandKey = currentBrand ? `${currentBrand}_` : "";
        const vlaCompletedKey = `${brandKey}vla_completed_list`;
        const vlaDraftsKey = `${brandKey}vla_drafts_list`;

        const vlaListStr = clientStorage.getItem(vlaCompletedKey);
        let vlaList = [];
        if (vlaListStr) {
          try {
            vlaList = JSON.parse(vlaListStr);
          } catch (e) {}
        }

        const vlaDraftsStr = clientStorage.getItem(vlaDraftsKey);
        if (vlaDraftsStr) {
          try {
            const drafts = JSON.parse(vlaDraftsStr);
            drafts.forEach((d) => {
              if (!vlaList.some((v) => v.id === d.id)) {
                vlaList.push({
                  id: d.id,
                  applicant_name: d.applicant_name,
                  completed_at: d.updated_at,
                  form_data: d.form_data,
                  type: "VLA_DRAFT",
                });
              }
            });
          } catch (e) {}
        }
        setCompletedVlas(vlaList);
      } catch (e) {
        console.error(
          "Failed to load completed VLA apps for guidelines prefill:",
          e,
        );
      }
    };

    loadVlas();
    window.addEventListener("storage", loadVlas);
    return () => window.removeEventListener("storage", loadVlas);
  }, [currentBrand]);

  const handlePrefillFromVla = (id) => {
    const match = completedVlas.find((v) => v.id === id);
    if (match && match.form_data) {
      const fd = match.form_data;
      setForm((prev) => ({
        ...prev,
        client_name:
          `${fd.first_name || ""} ${fd.last_name || ""}`.trim() ||
          prev.client_name,
        client_dob: fd.dob || prev.client_dob,
        file_ref: fd.file_number || fd.file_no || prev.file_ref,
      }));
      toast.success(
        `Prefilled details from ${fd.first_name || "Client"}'s VLA Application.`,
      );
    }
  };

  const handleClearPrefill = () => {
    setPrefillId("");
    setForm((prev) => ({
      ...prev,
      client_name: "",
      client_dob: "",
      file_ref: "",
    }));
    toast.success("Cleared prefilled client details.");
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCriteriaChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      criteria: { ...prev.criteria, [key]: value },
    }));
  };

  const handleGuidelineChange = (value) => {
    setForm((prev) => ({ ...prev, guideline: value, criteria: {} }));
  };

  const handleClear = () => {
    setForm(emptyForm);
    setBoeFile(null);
    setPrefillId("");
  };

  const saveGuidelineToLocalStorage = () => {
    try {
      const completedInfo = {
        id: `guideline-completed-${crypto.randomUUID()}`,
        client_name: form.client_name || "Unnamed Client",
        guideline: form.guideline || "General Guideline",
        completed_at: new Date().toISOString(),
        form_data: form,
        type: "Guideline",
      };
      const brandKey = currentBrand ? `${currentBrand}_` : "";
      const guidelinesCompletedKey = `${brandKey}guidelines_completed_list`;
      const demographicsKey = `${brandKey}active_client_demographics`;
      const existingStr = clientStorage.getItem(guidelinesCompletedKey);
      let list = [];
      if (existingStr) {
        try {
          list = JSON.parse(existingStr);
        } catch (e) {}
      }
      list = list.filter(
        (item) =>
          !(
            item.client_name === completedInfo.client_name &&
            item.guideline === completedInfo.guideline
          ),
      );
      list.push(completedInfo);
      clientStorage.setItem(guidelinesCompletedKey, JSON.stringify(list));

      // Save client demographics back so other forms can pre-fill
      const nameParts = (form.client_name || "").trim().split(/\s+/);
      const first = nameParts[0] || "";
      const last = nameParts.slice(1).join(" ") || "";
      const clientDemographics = {
        first_name: first,
        surname: last,
        dob: form.client_dob || "",
        file_no: form.file_ref || "",
        lawyer: form.lawyer || "",
      };
      clientStorage.setItem(
        demographicsKey,
        JSON.stringify(clientDemographics),
      );

      // Write to standalone outbox database so it lands in the Submissions Inbox
      try {
        const brandName = brandObj.appName || "VALS";
        const emailSubject = `${brandName} Guideline Certification - ${form.client_name || "Unnamed Client"} - ${form.guideline || "General Guideline"}`;

        let criteriaSummary = "";
        if (form.criteria) {
          criteriaSummary = Object.entries(form.criteria)
            .filter(([_, val]) => val === true)
            .map(([key]) => `- ${key}`)
            .join("\n");
        }

        const emailBody = `
Guideline Certification Checklist
---------------------------------
Client Name: ${form.client_name || "Unnamed Client"}
DOB: ${form.client_dob || "N/A"}
File Ref: ${form.file_ref || "N/A"}
Lawyer: ${form.lawyer || "N/A"}
Certified By: ${form.certified_by || "N/A"}
Date: ${form.cert_date || "N/A"}
Guideline: ${form.guideline || "N/A"}
Waiver Applies: ${form.waiver_applies ? "Yes" : "No"}

Selected Compliance Criteria:
${criteriaSummary || "- No criteria selected"}

Notes:
${form.notes || "No notes provided"}
        `.trim();

        base44.integrations.Core.SendEmail({
          to: INTAKE_EMAIL,
          cc: "",
          subject: emailSubject,
          body: emailBody,
        }).catch((err) =>
          console.warn(
            "Failed to write Guideline Certification to outbox:",
            err,
          ),
        );
      } catch (emailErr) {
        console.warn(
          "Failed to write Guideline Certification outbox:",
          emailErr,
        );
      }

      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error(
        "Failed to save completed guideline checklist to clientStorage:",
        e,
      );
    }
  };

  const handleSavePDF = () => {
    setShowDialog(false);
    saveGuidelineToLocalStorage();
    // Preserve the established certification layout. The browser print dialog's
    // "Save as PDF" destination uses the same print stylesheet as hard-copy printing.
    setTimeout(() => window.print(), 300);
  };

  const selectedGuideline = GUIDELINES[form.guideline];
  const selectedCriteria =
    selectedGuideline?.criteria
      ?.flatMap((group) => group.items)
      .filter((item) => form.criteria?.[item.id]) || [];

  const createCertificationPdf = () =>
    createGuidelineCertificationPdf(form, { appName: brandObj.appName, brandColor: primary });

  const createCertificationDraftLink = () => {
    const bytes = new TextEncoder().encode(JSON.stringify(form));
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    const url = new URL(
      `/${currentBrand}-certification`,
      window.location.origin,
    );
    url.searchParams.set("draft_data", btoa(binary));
    return url.toString();
  };

  const handlePrint = () => {
    setShowDialog(false);
    saveGuidelineToLocalStorage();
    setTimeout(() => window.print(), 300);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailTo) return;
    setEmailing(true);
    try {
      const criteriaSummary = selectedCriteria
        .map((item) => `- ${item.text}`)
        .join("\n");

      let emailBody = `
${brandObj.appName} GUIDELINE CERTIFICATION
==========================================

CLIENT DETAILS
--------------
Client Name: ${form.client_name || "Unnamed Client"}
DOB: ${form.client_dob || "N/A"}
File Ref: ${form.file_ref || "N/A"}

GUIDELINE SELECTION & CRITERIA
------------------------------
Selected Guideline: ${selectedGuideline?.label || form.guideline || "N/A"}

Selected Compliance Criteria:
${criteriaSummary || "- No criteria selected"}

Means Test Waiver Applies: ${form.waiver_applies ? "Yes" : "No"}

CERTIFICATION DETAILS
---------------------
Lawyer: ${form.lawyer || "N/A"}
Certified By: ${form.certified_by || "N/A"}
Date: ${form.cert_date || "N/A"}

Additional Certification Notes:
${form.notes || "No notes provided"}

Brief of Evidence (BOE): ${form.boe_name || "Not attached"}
      `.trim();

      if (emailMsg) {
        emailBody += `\n\nMESSAGE FROM SENDER:\n--------------------\n${emailMsg}`;
      }

      const draftLink = createCertificationDraftLink();
      emailBody += `\n\nOPEN THE EDITABLE DRAFT CERTIFICATION:\n${draftLink}`;
      const pdf = createCertificationPdf();
      const response = await fetch("/api/send-form-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipientEmail: emailTo,
          ccEmail: emailCc || "",
          message: emailBody,
          formName: `Guideline Certification - ${form.client_name || "Client"}`,
          pdfBase64: pdf.output("datauristring").split(",")[1],
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Email delivery failed.");
      }

      toast.success(
        "Certification email sent with the editable draft link and PDF attached.",
      );
      saveGuidelineToLocalStorage();
      setShowEmailDialog(false);
      setEmailTo("");
      setEmailCc("");
      setEmailMsg("");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to send certification email.");
    } finally {
      setEmailing(false);
    }
  };

  const ADMIN_GROUPS = [
    {
      title: "General System Admin",
      description: "Core administrative workflows and global configurations.",
      // Emma rarely uses these (21 Oct 2026), so the section starts minimised; a search
      // still finds its tools.
      collapsedByDefault: true,
      tabs: [
        "matter-workspace",
        "inbox",
        "weekly-summary",
        "certifications",
        "draft-forms",
        "deadline-guard",
        "audit-logs",
      ],
    },
    {
      title: "LACW",
      description:
        "Tools and trackers specific to Law & Advocacy Centre for Women.",
      tabs: [
        "lacw-client-account",
        "lacw-fee-slip-invoice-payment",
        "lacw-trust-receipts",
        "lacw-emails",
        "billing",
        "lacw-aid-planner",
        "lacw-staff-calendar",
        "lacw-appeal-cost-certificate",
      ],
    },
    {
      title: "RGA",
      description:
        "Tools and trackers specific to Robyn Greensill & Associates.",
      tabs: [
        "rga-fee-slip-invoice-payment",
        "rga-trust-receipts",
        "rga-emails",
        "appeal-cost-certificate",
      ],
    },
    {
      title: "VALS",
      description: "Victorian Aboriginal Legal Service workspace.",
      tabs: [
        "vals-client-account",
        "vals-fee-slip-invoice-payment",
        "vals-trust-receipts",
        "vals-emails",
        "vals-appeal-cost-certificate",
        "vals-diary",
      ],
    },
    {
      title: "EAS",
      description: "Tools and trackers specific to EAS.",
      tabs: [
        "eas-client-account",
        "eas-fee-slip-invoice-payment",
        "eas-trust-receipts",
        "eas-diary",
        "eas-emails",
        "eas-appeal-cost-certificate",
      ],
    },
  ];

  const normalizedToolSearch = toolSearch.trim().toLowerCase();
  const matchesToolSearch = (tab) =>
    !normalizedToolSearch ||
    `${tab.label} ${tab.description || ""}`
      .toLowerCase()
      .includes(normalizedToolSearch);
  // Simulation tools work on fictional data. Staff do not see them; administrators and
  // preview test mode do, in their own "Testing" group so they are not mistaken for real
  // tools.
  const canSeeTestingTools = user?.role === "admin" || isLacwLocalTestMode();
  const filteredPortalTabs = portalTabs.filter(
    (tab) => !tab.hidden && matchesToolSearch(tab) && (!tab.testing || canSeeTestingTools),
  );
  const staffPortalTabs = filteredPortalTabs.filter((tab) => !tab.testing);
  const renderPortalTile = (tab) => {
    const IconComp = tab.icon;
    return (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        className="portal-tile-btn group flex min-h-56 h-full w-full flex-col justify-between rounded-xl border border-neutral-200 bg-white p-6 text-left transition hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2"
      >
        <div className="w-full">
          {/* Icon container */}
          {tab.logo ? (
            <div className="mb-4 flex h-10 items-center justify-start">
              <img
                src={tab.logo}
                alt={tab.label}
                className="h-8 max-w-[150px] object-contain"
              />
            </div>
          ) : (
            <div
              className="mb-4 flex h-10 w-10 items-center justify-center"
              style={{
                backgroundColor: `${primary}15`,
                color: primary,
              }}
            >
              {IconComp && <IconComp className="w-5 h-5" />}
            </div>
          )}
          {/* Title */}
          <h2 className="text-base font-semibold text-neutral-900 group-hover-title transition-colors">
            {tab.label}
          </h2>
          {/* Description */}
          <p className="mt-2 text-sm text-neutral-500 leading-6">
            {tab.description}
          </p>
        </div>
        {/* Action Link */}
        <p
          className="mt-4 text-xs font-medium"
          style={{ color: primary }}
        >
          Open tool <span aria-hidden="true">→</span>
        </p>
      </button>
    );
  };
  const testingPortalTabs = filteredPortalTabs.filter((tab) => tab.testing);
  const filteredAdminGroups = ADMIN_GROUPS.map((group) => ({
    ...group,
    visibleTabs: portalTabs.filter(
      (tab) => group.tabs.includes(tab.id) && matchesToolSearch(tab),
    ),
  })).filter((group) => group.visibleTabs.length > 0);
  const visibleToolCount =
    currentBrand === "admin"
      ? filteredAdminGroups.reduce(
          (count, group) => count + group.visibleTabs.length,
          0,
        )
      : filteredPortalTabs.length;
  const portalTitle = {
    admin: "CLS Portal",
    eas: "EAS Legal Portal",
    vals: "VALS Portal",
    lacw: "LACW Portal",
    jbl: "Jarrod Behan Law Portal",
    rga: "RGA Portal",
    counsel: "Counsel Portal",
  }[currentBrand];

  return (
    <div
      className={`min-h-screen bg-white dark:bg-gray-950 print:bg-white print:min-h-0 ${activePortalTab === "vals-diary" || activePortalTab === "eas-diary" ? "vals-diary-print-host" : ""}`}
    >
      {currentBrand === "lacw" && <LacwLocalTestBar />}
      {/* Header — hidden on the portal dashboard to avoid double headers */}
      {(!isPortalMode || activePortalTab !== "dashboard") && (
        <header
          className="bg-white border-b-4 py-4 print:py-3 shadow-sm"
          style={{ borderBottomColor: primary }}
        >
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            {isPortalMode &&
            activePortalTab !== "dashboard" &&
            !window.location.search.includes("draft") ? (
              <button
                onClick={() => handleTabChange("dashboard")}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 transition-colors text-sm font-semibold uppercase tracking-wide print:hidden animate-fade-in"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Portal</span>
              </button>
            ) : backUrl && !window.location.search.includes("draft") ? (
              <a
                href={backUrl}
                target={window.top !== window.self ? "_top" : "_self"}
                onClick={() => {
                  if (window.self === window.top) {
                    try {
                      window.close();
                    } catch (err) {}
                  }
                }}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 transition-colors text-sm font-semibold uppercase tracking-wide print:hidden animate-fade-in"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Portal</span>
              </a>
            ) : (
              <div className="w-24 print:hidden" />
            )}
            <div className="text-center flex-1">
              <div className="flex flex-col items-center gap-2">
                {currentBrand === "lacw" ? (
                  <LacwWordmark compact />
                ) : currentBrand === "rga" ? (
                  <RgaWordmark compact />
                ) : brandObj.logoUrl ? (
                  <img
                    src={brandObj.logoUrl}
                    alt={brandObj.appName}
                    className="h-10 md:h-12 w-auto object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center leading-none">
                    <span className="text-sm font-semibold tracking-[0.1em] text-neutral-900 dark:text-white uppercase">
                      CLS
                    </span>
                    <span
                      className="text-[10px] font-bold uppercase mt-1"
                      style={{ color: primary }}
                    >
                      Admin Portal
                    </span>
                  </div>
                )}
                <p className="text-slate-400 text-[9px] font-bold uppercase tracking-[0.25em]">
                  {getSubTitle()}
                </p>
              </div>
            </div>
            <div className="w-24 print:hidden flex justify-end">
              {user && (
                <button
                  onClick={() => logout(true)}
                  className="flex items-center gap-1.5 text-slate-500 hover:text-red-500 transition-colors text-xs font-bold uppercase tracking-wider cursor-pointer"
                  title={`Logged in as ${user.full_name} (${user.role})`}
                >
                  <Shield className="w-3.5 h-3.5" />
                  <span>Logout</span>
                </button>
              )}
            </div>
          </div>
        </header>
      )}

      {isPortalMode && activePortalTab === "dashboard" && (
        <RoleGuard
          user={user}
          allowedRoles={currentBrand === "admin" ? ["admin"] : undefined}
        >
          <main className="mx-auto w-full max-w-6xl bg-white px-5 py-10 sm:px-8 sm:py-12 lg:px-10">
            <p className="mb-2 text-xs uppercase tracking-[0.3em] text-neutral-500">
              {portalTitle}
            </p>
            <div
              className="mb-6 h-[1.5px] w-12"
              style={{ backgroundColor: primary }}
            />

            {/* What is outstanding across the firms, urgent first, before the tools. */}
            {currentBrand === "admin" && (
              <MorningSummary primary={primary} onOpen={handleTabChange} />
            )}

            <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Your Tools
            </h1>
            <p className="mt-3 text-sm text-neutral-500 leading-6">
              Access your workflow tools and resources below.
            </p>

            {portalTabs.length > 6 && (
              <div className="relative mt-8 max-w-xl">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                  aria-hidden="true"
                />
                <label htmlFor="portal-tool-search" className="sr-only">
                  Search tools
                </label>
                <input
                  id="portal-tool-search"
                  type="search"
                  value={toolSearch}
                  onChange={(event) => setToolSearch(event.target.value)}
                  placeholder="Search tools by name or purpose"
                  className="h-12 w-full rounded-lg border border-neutral-300 bg-white pl-11 pr-11 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-500 focus:ring-2 focus:ring-neutral-200"
                />
                {toolSearch && (
                  <button
                    type="button"
                    onClick={() => setToolSearch("")}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
                    aria-label="Clear tool search"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}

            {visibleToolCount === 0 && (
              <div className="mt-10 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-6 py-12 text-center">
                <p className="text-sm font-semibold text-neutral-800">
                  No tools match “{toolSearch}”
                </p>
                <button
                  type="button"
                  onClick={() => setToolSearch("")}
                  className="mt-3 text-sm font-medium underline underline-offset-4"
                  style={{ color: primary }}
                >
                  Clear search
                </button>
              </div>
            )}

            {visibleToolCount > 0 &&
              (currentBrand === "admin" ? (
                <div className="mt-10 space-y-12">
                  {filteredAdminGroups.map((group) => {
                    const groupTabs = group.visibleTabs;
                    const minimisable = Boolean(group.collapsedByDefault);
                    const groupOpen = !minimisable || Boolean(normalizedToolSearch) || openAdminGroups.has(group.title);
                    const toggleGroup = () => setOpenAdminGroups((current) => {
                      const next = new Set(current);
                      if (next.has(group.title)) next.delete(group.title);
                      else next.add(group.title);
                      return next;
                    });
                    return (
                      <section
                        key={group.title}
                        className="border-t border-neutral-200 pt-8 first:border-t-0 first:pt-0"
                      >
                        <div className={`${groupOpen ? "mb-6" : ""} flex items-start justify-between gap-4`}>
                          <div>
                            <h2 className="text-xl font-bold tracking-tight text-neutral-900">
                              {group.title}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-neutral-500">
                              {group.description}
                            </p>
                          </div>
                          {minimisable && !normalizedToolSearch && (
                            <button
                              type="button"
                              onClick={toggleGroup}
                              aria-expanded={groupOpen}
                              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-400"
                            >
                              {groupOpen ? "Hide" : `Show ${groupTabs.length} tool${groupTabs.length === 1 ? "" : "s"}`}
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${groupOpen ? "rotate-180" : ""}`} />
                            </button>
                          )}
                        </div>

                        {!groupOpen ? null : groupTabs.length > 0 ? (
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {groupTabs.map((tab) => {
                              const IconComp = tab.icon;
                              return (
                                <button
                                  key={tab.id}
                                  onClick={() => handleTabChange(tab.id)}
                                  className="portal-tile-btn group flex min-h-56 h-full w-full flex-col justify-between rounded-xl border border-neutral-200 bg-white p-6 text-left transition hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2"
                                >
                                  <div className="w-full">
                                    {/* Icon container */}
                                    {tab.logo ? (
                                      <div className="mb-4 flex h-10 items-center justify-start">
                                        <img
                                          src={tab.logo}
                                          alt={tab.label}
                                          className="h-8 max-w-[150px] object-contain"
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className="mb-4 flex h-10 w-10 items-center justify-center"
                                        style={{
                                          backgroundColor: `${primary}15`,
                                          color: primary,
                                        }}
                                      >
                                        {IconComp && (
                                          <IconComp className="w-5 h-5" />
                                        )}
                                      </div>
                                    )}
                                    {/* Title */}
                                    <h2 className="text-base font-semibold text-neutral-900 group-hover-title transition-colors">
                                      {tab.label}
                                    </h2>
                                    {/* Description */}
                                    <p className="mt-2 text-sm text-neutral-500 leading-6">
                                      {tab.description}
                                    </p>
                                  </div>
                                  {/* Action Link */}
                                  <p
                                    className="mt-4 text-xs font-medium"
                                    style={{ color: primary }}
                                  >
                                    Open tool <span aria-hidden="true">→</span>
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-8 border border-dashed border-neutral-200 rounded-lg bg-neutral-50 flex flex-col items-center justify-center text-center">
                            <span className="text-xs text-neutral-400 font-medium">
                              No tools currently active
                            </span>
                            <span className="mt-1 text-[10px] text-neutral-350">
                              Custom client configurations will be registered
                              here.
                            </span>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {staffPortalTabs.map((tab) => renderPortalTile(tab))}
                  </div>
                  {testingPortalTabs.length > 0 && (
                    <section className="mt-12" aria-labelledby="testing-tools-heading">
                      <h2 id="testing-tools-heading" className="text-sm font-semibold uppercase tracking-wider text-amber-800">
                        Testing tools
                      </h2>
                      <p className="mt-1 text-sm text-neutral-500">
                        Simulations with fictional data, for testing and demonstrations. Staff do not see these.
                      </p>
                      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {testingPortalTabs.map((tab) => renderPortalTile(tab))}
                      </div>
                    </section>
                  )}
                </>
              ))}
          </main>
        </RoleGuard>
      )}

      {activePortalTab === "matter-workspace" && (
        <MatterWorkspaceView
          brandType={currentBrand}
          onTabChange={onTabChange}
        />
      )}

      {activePortalTab === "certification" && (
        <main
          id="eas-legal-form"
          ref={mainContentRef}
          className="max-w-4xl mx-auto py-8 px-4"
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-slate-200/40 p-8 print:shadow-none print:p-0 border border-slate-100">
            {completedVlas.length > 0 && (
              <div className="mb-6 p-4 bg-slate-50/50 border border-slate-100 rounded-2xl print:hidden flex flex-col md:flex-row md:items-center justify-between gap-3 animate-fade-in">
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                    Quick Fill Profile
                  </span>
                  <span className="text-[11px] text-slate-400">
                    Prefill certification fields using client details from a
                    completed VLA form.
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={prefillId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPrefillId(val);
                      handlePrefillFromVla(val);
                    }}
                    className="text-xs bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent min-w-[240px] shadow-sm cursor-pointer font-bold"
                    style={{ focusRingColor: primary }}
                  >
                    <option value="" disabled>
                      Select VLA Application...
                    </option>
                    {completedVlas.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.applicant_name} (
                        {v.type === "VLA_DRAFT" ? "Draft" : "Completed"})
                      </option>
                    ))}
                  </select>
                  {prefillId && (
                    <button
                      type="button"
                      onClick={handleClearPrefill}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-xl transition-all flex items-center justify-center"
                      title="Clear selection"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <AdminDetailsSection form={form} onChange={handleChange} />
            <GuidelineSection
              form={form}
              onGuidelineChange={handleGuidelineChange}
              onCriteriaChange={handleCriteriaChange}
              onChange={handleChange}
              brand={currentBrand}
              boeFile={boeFile}
              onBoeFileChange={setBoeFile}
            />

            {/* Buttons */}
            <div className="flex flex-wrap justify-end items-center gap-3 mt-8 pt-6 border-t border-slate-100 print:hidden">
              <ShareDraftButtons
                formData={form}
                onImport={setForm}
                brand={brandType}
                formName="certification_sheet"
              />
              <button
                onClick={handleClear}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase tracking-wide text-xs hover:bg-slate-50 transition-all select-none"
              >
                Reset Form
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmailTo(INTAKE_EMAIL);
                  setShowEmailDialog(true);
                }}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase tracking-wide text-xs hover:bg-slate-50 transition-all select-none flex items-center gap-1.5"
              >
                <Mail className="w-3.5 h-3.5" /> Email Checklist
              </button>
              <ActionstepSavePanel
                noteText={[
                  `GUIDELINE CERTIFICATION — ${currentBrand?.toUpperCase() || "VALS"}`,
                  ``,
                  `CLIENT: ${form.client_name || "N/A"}`,
                  `LAWYER: ${form.lawyer_name || form.solicitor_name || "N/A"}`,
                  `MATTER TYPE: ${form.matter_type || "N/A"}`,
                  `COURT: ${form.court || "N/A"}`,
                  `DATE: ${form.cert_date || new Date().toLocaleDateString("en-AU")}`,
                  `GUIDELINE: ${selectedGuideline?.label || form.guideline || "N/A"}`,
                  `BOE: ${form.boe_name || "Not attached"}`,
                ].join("\n")}
                formLabel="Guideline Certification"
                accentColor={secondary}
                disabled={!form.client_name}
                // No file note: the certification sheet is the artefact, and a note
                // summarising it would put a second, lesser copy of the same thing in the
                // matter chronology.
                saveNote={false}
                // The certification sheet is a document, not a note about one. A file note
                // records that certification happened; the firm needs the completed sheet
                // itself on the matter, which is what they would have to produce later.
                // Built at save time so it reflects the form as submitted.
                getDocuments={async () => {
                  const pdf = createCertificationPdf();
                  const certificationFile = new File(
                    [pdf.output("blob")],
                    portalDocumentName("Guideline-Certification", form.client_name, form.cert_date),
                    { type: "application/pdf" },
                  );
                  // Only the certification sheet. The brief of evidence and any other
                  // attachment is very likely already on the matter in Actionstep - it is
                  // where the firm got it from - so uploading it again would create a
                  // duplicate rather than add anything.
                  return [
                    { file: certificationFile, folder: actionstepFolderFor("certification") },
                  ];
                }}
              />
              <button
                onClick={() => setShowDialog(true)}
                className="px-6 py-2.5 text-white rounded-xl font-bold uppercase tracking-wide text-xs flex items-center gap-2 shadow-xl shadow-slate-200 transition-all select-none"
                style={{ backgroundColor: secondary }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9V2h12v7" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                Generate Certificate
              </button>
            </div>
          </div>
        </main>
      )}

      {activePortalTab === "aid-form" && (
        <LegalAidForm
          isPortalMode={true}
          backUrl={backUrl}
          brandType={currentBrand}
        />
      )}

      {activePortalTab === "intake" && (
        <VALSIntakeForm isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "authority" && (
        <VALSAuthorityToAct isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "backsheet" && (
        <div id="eas-legal-form">
          <BacksheetToCounsel isPortalMode={true} brand={currentBrand} />
        </div>
      )}

      {activePortalTab === "medical-worksheet" && (
        <VLAReportWorksheet isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "memo-precedent" && (
        <MemoPrecedent isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "apply-aid" && (
        <ApplyForAid isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "appeal-cost-certificate" && (
        <AppealCostCertificate
          isPortalMode={true}
          brandType={currentBrand === "admin" ? "rga" : currentBrand}
          isAdminMode={currentBrand === "admin"}
        />
      )}

      {activePortalTab === "lacw-appeal-cost-certificate" && (
        <AppealCostCertificate
          isPortalMode={true}
          brandType="lacw"
          isAdminMode={true}
        />
      )}

      {activePortalTab === "vals-appeal-cost-certificate" && (
        <AppealCostCertificate
          isPortalMode={true}
          brandType="vals"
          isAdminMode={true}
        />
      )}

      {activePortalTab === "eas-appeal-cost-certificate" && (
        <AppealCostCertificate
          isPortalMode={true}
          brandType="eas"
          isAdminMode={true}
        />
      )}

      {activePortalTab === "travel-claim" && (
        <TravelClaims isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "additional-prep" && (
        <AdditionalPrepWorksheet isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "billing" && <LACWBilling isPortalMode={true} />}
      {activePortalTab === "aid-diary" && currentBrand === "lacw" && <LACWAidDiary />}
      {activePortalTab === "aid-planner" && (currentBrand === "lacw" || currentBrand === "vals") && (
        <AidPlanner embedded brand={currentBrand} />
      )}
      {/* LACW's Aid Planner, worked from the admin portal. brand="lacw" files every matter
          under LACW, so LACW staff see in their own portal what is done here. */}
      {activePortalTab === "lacw-aid-planner" && currentBrand === "admin" && (
        <AidPlanner embedded brand="lacw" />
      )}
      {activePortalTab === "aid-extension-flowchart" && currentBrand === "lacw" && (
        <AidExtensionFlowchart />
      )}

      {(activePortalTab === "fee-slip-invoice-payment" ||
        activePortalTab === "lacw-fee-slip-invoice-payment" ||
        activePortalTab === "rga-fee-slip-invoice-payment" ||
        activePortalTab === "vals-fee-slip-invoice-payment" ||
        activePortalTab === "eas-fee-slip-invoice-payment") && (
        <FeeSlipInvoicePayment
          isPortalMode={true}
          brandType={
            activePortalTab === "lacw-fee-slip-invoice-payment"
              ? "lacw"
              : activePortalTab === "rga-fee-slip-invoice-payment"
                ? "rga"
                : activePortalTab === "vals-fee-slip-invoice-payment"
                  ? "vals"
                  : activePortalTab === "eas-fee-slip-invoice-payment"
                    ? "eas"
                    : currentBrand
          }
          isAdminMode={currentBrand === "admin"}
        />
      )}

      {/* New file requests, one page per firm. This used to be a separate page framed in
          here (public/*-portal-tool); it is now a portal page like the others. */}
      {(activePortalTab === "lacw-emails" ||
        activePortalTab === "vals-emails" ||
        activePortalTab === "rga-emails" ||
        activePortalTab === "eas-emails") && (
        <LegalAidStatusMonitor brand={activePortalTab.replace("-emails", "")} />
      )}

      {activePortalTab === "draft-forms" && (
        <DraftForms onTabChange={onTabChange} brandType={currentBrand} />
      )}

      {activePortalTab === "vals-diary" && <VALSDiary isPortalMode={true} />}
      {activePortalTab === "eas-diary" && <VALSDiary isPortalMode={true} firm="eas" />}
      {activePortalTab === "diary-codes" && (currentBrand === "lacw" || currentBrand === "vals") && <DiaryCodesSheet brand={currentBrand} />}
      {activePortalTab === "reminders-guide" && (currentBrand === "lacw" || currentBrand === "vals") && <RemindersGuide brand={currentBrand} />}

      {activePortalTab === "staff-calendar" && currentBrand === "lacw" && (
        <LACWStaffCalendar />
      )}
      {/* LACW's calendar, worked from the admin portal. Every entry is saved under LACW
          (buildCalendarDraft), so LACW staff see in their own portal what is added here. */}
      {activePortalTab === "lacw-staff-calendar" && currentBrand === "admin" && (
        <LACWStaffCalendar />
      )}

      {activePortalTab === "outlook-mail" && currentBrand === "lacw" && (
        <OutlookMail />
      )}

      {/* The firm's own view. brand seeds the filter; the API decides what this caller may
          actually see, so it cannot be widened by changing the prop. */}
      {activePortalTab === "request-queue" && currentBrand === "lacw" && (
        <RequestQueue brand="lacw" />
      )}

      {/* An administrator picks the firm on the page; a firm sees only its own. */}
      {activePortalTab === "certifications" && (
        <CertificationReview brand={currentBrand === "admin" ? undefined : currentBrand} />
      )}


      {/* The simulator keeps fictional matters in this browser and refuses to run on the live
          portal, so an administrator opening it there gets a note instead of a blank page. */}
      {activePortalTab === "actionstep-simulator" && currentBrand === "lacw" && canSeeTestingTools && (
        isLacwLocalTestMode() ? (
          <ActionstepSimulator />
        ) : (
          <div className="mx-auto max-w-2xl rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            <p className="font-bold">The Actionstep Simulator only runs in the LACW test preview.</p>
            <p className="mt-2 leading-6">
              It uses fictional matters stored in your browser, so it is switched off on the live portal
              to keep test data away from real records. Open it from a test preview link instead.
            </p>
          </div>
        )
      )}

      {activePortalTab === "atlas-simulator" && currentBrand === "lacw" && canSeeTestingTools && (
        <AtlasSimulator />
      )}

      {activePortalTab === "atlas-letter-downloader" && currentBrand === "lacw" && canSeeTestingTools && (
        <AtlasLetterDownloader />
      )}

      {/* Disbursements (reports, interpreters, transcripts) for LACW, VALS and EAS. RGA's tab
          is still its trust receipts and client trust deposits. */}
      {activePortalTab === "monthly-trust-receipts" && (
        currentBrand === "rga" ? (
          <MonthlyTrustReceipts
            isPortalMode={true}
            brandType={currentBrand}
            isRgaPortal={currentBrand === "rga"}
          />
        ) : (
          <DisbursementsTracker brandType={currentBrand} readOnly={currentBrand === "lacw" && user?.role !== "admin"} />
        )
      )}

      {activePortalTab === "rga-trust-receipts" && (
        <MonthlyTrustReceipts isPortalMode={true} brandType="rga" />
      )}

      {activePortalTab === "vals-trust-receipts" && (
        <DisbursementsTracker brandType="vals" />
      )}

      {activePortalTab === "eas-trust-receipts" && (
        <DisbursementsTracker brandType="eas" />
      )}

      {activePortalTab === "lacw-trust-receipts" && (
        <DisbursementsTracker brandType="lacw" />
      )}

      {activePortalTab === "submitted-invoices" && ["lacw", "vals", "eas"].includes(currentBrand) && (
        <SubmittedInvoices brandType={currentBrand} />
      )}

      {activePortalTab === "conflict-check" && (
        <ConflictCheck isPortalMode={true} brandType={currentBrand} />
      )}

      {activePortalTab === "inbox" && (
        <RoleGuard user={user} allowedRoles={["admin"]}>
          {/* The admin inbox: requests submitted from the portal tools (Apply for Aid's
              Submit Request, travel claims...), across every firm. */}
          <RequestQueue title="Admin inbox" />
        </RoleGuard>
      )}

      {/* What was actioned for each firm in a week, to print for the firm. */}
      {activePortalTab === "weekly-summary" && currentBrand === "admin" && (
        <RoleGuard user={user} allowedRoles={["admin"]}>
          <WeeklySummary primary={primary} onOpen={handleTabChange} />
        </RoleGuard>
      )}

      {activePortalTab === "audit-logs" && (
        <RoleGuard user={user} allowedRoles={["admin"]}>
          <AdminAuditLogsView />
        </RoleGuard>
      )}

      {activePortalTab === "deadline-guard" && (
        <div className="h-[calc(100dvh-64px)] min-h-0 overflow-hidden bg-slate-50 p-3 sm:p-6">
          <div style={{ width: "100%", height: "100%", minHeight: 0, background: "#fff" }} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              src={getWorkflowDashboardUrl()}
              title="Deadline Guard Workflow Dashboard"
              style={{ display: "block", width: "100%", height: "100%", border: "none" }}
              allow="clipboard-write"
            />
          </div>
        </div>
      )}

      {(activePortalTab === "lacw-client-account" ||
        activePortalTab === "vals-client-account" ||
        activePortalTab === "eas-client-account") && (
        <ClientAccountDashboard clientId={activePortalTab.replace("-client-account", "")} />
      )}

      {activePortalTab === "training-guide" && <JBLPortalGuide />}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="rounded-3xl border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A1F2C]">
              Export Certification
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-sm">
            Your browser's print dialog will open. To save as a PDF, select{" "}
            <strong>"Save as PDF"</strong> as the destination.
          </p>
          <DialogFooter className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={handleSavePDF}
              className="rounded-xl border-slate-200 text-slate-700 font-bold hover:bg-slate-50"
            >
              Save as PDF
            </Button>
            <Button
              onClick={handlePrint}
              className="rounded-xl text-white font-bold"
              style={{ backgroundColor: secondary }}
            >
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session drafts removed from certification sheets.
      <Dialog open={false}>
        <DialogContent className="rounded-3xl border-none shadow-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A1F2C]">Saved Guideline Drafts</DialogTitle>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-2 py-2">
            {draftsList.length > 0 ? (
              draftsList.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100/50 transition-colors">
                  <div className="cursor-pointer flex-grow" onClick={() => handleLoadDraft(d.id)}>
                    <p className="text-sm font-bold text-slate-800">{d.client_name || "Unnamed Client"}</p>
                    <p className="text-xs text-slate-400 font-semibold">{d.guideline || "General Guideline"} • {new Date(d.updated_at).toLocaleDateString("en-AU")}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleExportSavedDraft(d.id); }}
                      className="p-1 text-slate-400 hover:text-blue-500 rounded transition-colors"
                      title="Export Draft to File"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteDraft(d.id); }}
                      className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
                      title="Delete Draft"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4 italic">No saved drafts found.</p>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row justify-between items-center w-full gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current.click()}
              className="rounded-xl font-bold flex items-center gap-1.5 border-slate-200 w-full sm:w-auto"
            >
              <Upload className="w-4 h-4" /> Import Draft File
            </Button>
            <Button variant="outline" onClick={() => setShowDraftsDialog(false)} className="rounded-xl font-bold w-full sm:w-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportDraftFile}
        accept=".json"
        className="hidden"
      />
      */}

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handleSendEmail}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" style={{ color: primary }} />
                <span>Email Guideline Certification</span>
              </DialogTitle>
              <DialogDescription>
                Send an editable draft link and attach the completed
                certification as a PDF.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">
                  To <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="recipient@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="w-full border border-slate-350 rounded px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">
                  CC
                </label>
                <input
                  type="email"
                  placeholder="cc@example.com (optional)"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  className="w-full border border-slate-350 rounded px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">
                  Message
                </label>
                <textarea
                  placeholder="Add an optional message body..."
                  value={emailMsg}
                  onChange={(e) => setEmailMsg(e.target.value)}
                  className="w-full border border-slate-350 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y bg-white"
                  disabled={emailing}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
                className="rounded-xl font-bold"
                disabled={emailing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-xl text-white font-bold"
                style={{ backgroundColor: secondary }}
                disabled={emailing || !emailTo}
              >
                {emailing ? "Sending..." : "Send Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <style>{`
        /* Portal Tiles Styling to match EAS portal layout */
        .portal-tile-btn {
          border-radius: 0px !important;
        }
        .portal-tile-btn:hover {
          border-color: ${primary}b0 !important;
        }
        .portal-tile-btn:hover .group-hover-title {
          color: ${primary} !important;
        }

        /* Dynamic brand overrides — remap purple to brand specific colors */
        #eas-legal-form .text-purple-700,
        #eas-legal-form .text-purple-700 * { color: ${primary} !important; }
        #eas-legal-form .border-purple-700 { border-color: ${primary} !important; }
        #eas-legal-form .text-purple-400 { color: ${primary} !important; }
        #eas-legal-form .text-purple-500 { color: ${secondary} !important; }
        #eas-legal-form .border-purple-500,
        #eas-legal-form input:focus,
        #eas-legal-form textarea:focus,
        #eas-legal-form select:focus { border-color: ${primary} !important; outline-color: ${primary} !important; box-shadow: 0 0 0 2px ${focusShadow} !important; }
        #eas-legal-form .accent-purple-700,
        #eas-legal-form input[type="checkbox"] { accent-color: ${primary} !important; }
        #eas-legal-form .hover\\:text-gray-900:hover { color: ${secondary} !important; }

        ${
          currentBrand === "vals"
            ? `
        /* Earthy Ochre Red brand overrides inspired by Aboriginal artwork */
        .text-red-500, .text-red-600, .text-red-700, .text-red-800, .text-red-900 {
          color: ${primary} !important;
        }
        .bg-red-550, .bg-red-50, .bg-red-50\\/20, .bg-red-50\\/10 {
          background-color: ${primary}10 !important;
        }
        .bg-red-600, .bg-red-700, .bg-red-850, .bg-red-800 {
          background-color: ${primary} !important;
        }
        .border-red-100, .border-red-200, .border-red-300 {
          border-color: ${primary}30 !important;
        }
        .accent-red-600 {
          accent-color: ${primary} !important;
        }
        .hover\\:bg-red-50:hover, .hover\\:bg-red-50\\/50:hover {
          background-color: ${primary}15 !important;
        }
        .hover\\:bg-red-700:hover {
          background-color: ${primary}d0 !important;
        }
        .hover\\:border-red-200:hover, .hover\\:border-red-300:hover {
          border-color: ${primary}80 !important;
        }
        `
            : ""
        }

        ${
          currentBrand === "lacw"
            ? `
        /* Purple brand overrides for LACW */
        .text-red-500, .text-red-600, .text-red-700, .text-red-800, .text-red-900 {
          color: ${primary} !important;
        }
        .bg-red-550, .bg-red-50, .bg-red-50\\/20, .bg-red-50\\/10 {
          background-color: ${primary}10 !important;
        }
        .bg-red-600, .bg-red-700, .bg-red-850, .bg-red-800 {
          background-color: ${primary} !important;
        }
        .border-red-100, .border-red-200, .border-red-300 {
          border-color: ${primary}30 !important;
        }
        .accent-red-600 {
          accent-color: ${primary} !important;
        }
        .hover\\:bg-red-50:hover, .hover\\:bg-red-50\\/50:hover {
          background-color: ${primary}15 !important;
        }
        .hover\\:bg-red-700:hover {
          background-color: #7e22ce !important;
        }
        .hover\\:border-red-200:hover, .hover\\:border-red-300:hover {
          border-color: ${primary}80 !important;
        }
        `
            : ""
        }

        ${
          currentBrand === "jbl"
            ? `
        /* Gold & Charcoal brand overrides for JBL */
        .text-red-550, .text-red-50, .text-red-500, .text-red-600, .text-red-700, .text-red-800, .text-red-900 {
          color: ${primary} !important;
        }
        .bg-red-550, .bg-red-50, .bg-red-50\\/20, .bg-red-50\\/10 {
          background-color: ${primary}10 !important;
        }
        .bg-red-600, .bg-red-700, .bg-red-850, .bg-red-800 {
          background-color: ${primary} !important;
        }
        .border-red-100, .border-red-200, .border-red-300 {
          border-color: ${primary}30 !important;
        }
        .accent-red-600 {
          accent-color: ${primary} !important;
        }
        .hover\\:bg-red-50:hover, .hover\\:bg-red-50\\/50:hover {
          background-color: ${primary}15 !important;
        }
        .hover\\:bg-red-700:hover {
          background-color: ${primary}d0 !important;
        }
        .hover\\:border-red-200:hover, .hover\\:border-red-300:hover {
          border-color: ${primary}80 !important;
        }
        `
            : ""
        }

        ${
          currentBrand === "rga"
            ? `
        /* Teal brand overrides for Robyn Greensill and Associates */
        .text-red-550, .text-red-50, .text-red-500, .text-red-600, .text-red-700, .text-red-800, .text-red-900 {
          color: ${primary} !important;
        }
        .bg-red-550, .bg-red-50, .bg-red-50\\/20, .bg-red-50\\/10 {
          background-color: ${primary}10 !important;
        }
        .bg-red-600, .bg-red-700, .bg-red-850, .bg-red-800 {
          background-color: ${primary} !important;
        }
        .border-red-100, .border-red-200, .border-red-300 {
          border-color: ${primary}30 !important;
        }
        .accent-red-600 {
          accent-color: ${primary} !important;
        }
        .hover\\:bg-red-50:hover, .hover\\:bg-red-50\\/50:hover {
          background-color: ${primary}15 !important;
        }
        .hover\\:bg-red-700:hover {
          background-color: #0d9488 !important;
        }
        .hover\\:border-red-200:hover, .hover\\:border-red-300:hover {
          border-color: ${primary}80 !important;
        }
        `
            : ""
        }

        @media print {
          @page {
            size: A4;
            margin: 0;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          /* Hide all UI chrome */
          .print\\:hidden, nav, .fixed, button, dialog, [role="dialog"] {
            display: none !important;
          }

          body, html {
            background: #fff !important;
            margin: 0;
            padding: 12mm 12mm 15mm 12mm;
            font-family: 'Georgia', serif;
            font-size: 11pt;
            color: #1a1a1a;
            height: auto !important;
            min-height: 0 !important;
            overflow: hidden !important;
            max-height: 296mm !important;
          }

          /* ── Header ── */
          header {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            padding: 0 0 6pt 0 !important;
            margin-bottom: 8pt !important;
            border-bottom: 2pt solid ${primary} !important;
            background: transparent !important;
            box-shadow: none !important;
          }
          header img {
            height: 28pt !important;
            width: auto !important;
            object-fit: contain !important;
            margin-bottom: 2pt !important;
          }
          header p {
            font-family: 'Arial', sans-serif !important;
            font-size: 7.5pt !important;
            letter-spacing: 0.18em !important;
            text-transform: uppercase !important;
            color: #888 !important;
            margin: 0 !important;
          }

          /* ── Main wrapper ── */
          main {
            padding: 0 !important;
            background: transparent !important;
          }
          .rounded-2xl, .shadow-2xl {
            border-radius: 0 !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
          }

          /* ── Section headings (e.g. CLIENT DETAILS) ── */
          section > div:first-child {
            font-family: 'Arial', sans-serif !important;
            font-size: 7pt !important;
            font-weight: 700 !important;
            letter-spacing: 0.15em !important;
            text-transform: uppercase !important;
            color: ${primary} !important;
            border-bottom: 1pt solid ${primary} !important;
            padding-bottom: 2pt !important;
            margin-bottom: 6pt !important;
          }

          /* ── Field labels ── */
          label {
            font-family: 'Arial', sans-serif !important;
            font-size: 7pt !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.06em !important;
            color: #555 !important;
            display: block !important;
            margin-bottom: 1pt !important;
          }

          /* ── Text inputs and selects ── */
          input[type="text"], input[type="date"], select {
            display: block !important;
            width: 100% !important;
            border: none !important;
            border-bottom: 0.75pt solid #bbb !important;
            border-radius: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            padding: 1pt 0 !important;
            height: 18pt !important;
            font-family: 'Georgia', serif !important;
            font-size: 9.5pt !important;
            line-height: 1.2 !important;
            color: #1a1a1a !important;
            -webkit-appearance: none !important;
            appearance: none !important;
            outline: none !important;
          }

          input[type="date"]::-webkit-calendar-picker-indicator,
          input[type="date"]::-webkit-inner-spin-button,
          input[type="date"]::-webkit-clear-button {
            display: none !important;
            -webkit-appearance: none !important;
          }

          /* ── Textarea (notes) ── */
          textarea {
            display: block !important;
            width: 100% !important;
            border: 0.75pt solid #ddd !important;
            border-radius: 2pt !important;
            background: transparent !important;
            box-shadow: none !important;
            padding: 3pt 4pt !important;
            font-family: 'Georgia', serif !important;
            font-size: 9.5pt !important;
            color: #1a1a1a !important;
            resize: none !important;
            min-height: 40pt !important;
          }

          /* ── Checkboxes ── */
          input[type="checkbox"] {
            -webkit-appearance: none !important;
            appearance: none !important;
            width: 8pt !important;
            height: 8pt !important;
            border: 1pt solid #999 !important;
            border-radius: 1.5pt !important;
            background: transparent !important;
            position: relative !important;
            display: inline-block !important;
            flex-shrink: 0 !important;
            margin-top: 2pt !important;
            margin-right: 5pt !important;
            vertical-align: top !important;
          }
          input[type="checkbox"]:checked {
            background: ${primary} !important;
            border-color: ${primary} !important;
          }
          input[type="checkbox"]:checked::after {
            content: "✓" !important;
            position: absolute !important;
            top: -1.5pt !important;
            left: 0.5pt !important;
            font-size: 7.5pt !important;
            color: #fff !important;
            font-weight: bold !important;
          }

          /* ── Checkbox item rows ── */
          .flex.items-start {
            margin-bottom: 3pt !important;
            align-items: flex-start !important;
          }
          .flex.items-start span, .flex.items-start label {
            font-family: 'Georgia', serif !important;
            font-size: 9.5pt !important;
            line-height: 1.3 !important;
            color: #1a1a1a !important;
            text-transform: none !important;
            letter-spacing: 0 !important;
            font-weight: 400 !important;
            padding-left: 6pt !important;
          }

          /* ── Grid layout for fields ── */
          .grid { display: grid !important; gap: 6pt !important; }
          .md\\:grid-cols-3 { grid-template-columns: repeat(3, 1fr) !important; }
          .md\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr) !important; }

          /* ── Page footer ── */
          @page {
            @bottom-center {
              content: "${brandObj.appName} — Guideline Certification  |  Page " counter(page) " of " counter(pages);
              font-family: Arial, sans-serif;
              font-size: 7pt;
              color: #aaa;
            }
          }

          canvas { display: none !important; }
        }

        ${
          activePortalTab === "vals-diary"
            ? `
        @media print {
          @page {
            size: A4 landscape;
            margin: 6mm;
            @bottom-center { content: none; }
          }

          html, body {
            width: auto !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            font-family: Arial, sans-serif !important;
            font-size: 8pt !important;
          }

          .vals-diary-print-host > header,
          .vals-diary-print-host .no-print,
          .vals-diary-print-host .print-report-header {
            display: none !important;
          }

          .vals-diary-print-host,
          .vals-diary-print-root,
          .vals-diary-print-content {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: #fff !important;
          }
        }
        `
            : ""
        }

        ${
          // The LACW weekly billing sheet prints landscape. The A4 portrait page and the
          // Guideline Certification footer above are for the guideline forms, and would
          // otherwise override the sheet's own @page rule (this style tag comes later).
          activePortalTab === "billing"
            ? `
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
            @bottom-center { content: none; }
          }
        }
        `
            : ""
        }
      `}</style>
    </div>
  );
}
